import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppConfig } from '@/contexts/AppConfigContext';

// Definición de tipos para Facebook Pixel
interface FacebookPixel {
    (...args: any[]): void;
    callMethod?: (...args: any[]) => void;
    queue: any[];
    push: (...args: any[]) => void;
    loaded: boolean;
    version: string;
}

declare global {
    interface Window {
        dataLayer: any[];
        fbq?: FacebookPixel;
    }
}

export const AnalyticsLoader = () => {
    const location = useLocation();
    const { integration, config } = useAppConfig();
    const gtmId = config?.seo?.analytics?.googleTagManagerId;

    // 1. Immediate initialization for event buffering
    if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        if (!window.fbq) {
            const fbq: any = function () {
                fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
            };
            fbq.push = fbq;
            fbq.loaded = true;
            fbq.version = '2.0';
            fbq.queue = [];
            window.fbq = fbq;
        }
    }

    // 2. Resource Hints & Script Injection
    useEffect(() => {
        if (!integration?.analytics && !gtmId) return;

        // Add Resource Hints (using dns-prefetch for deferred resources)
        const domains = [
            'https://www.googletagmanager.com',
            'https://connect.facebook.net',
            'https://www.google-analytics.com'
        ];

        domains.forEach(domain => {
            if (document.querySelector(`link[href="${domain}"]`)) return;
            const link = document.createElement('link');
            // Use dns-prefetch instead of preconnect for deferred scripts
            // to avoid Lighthouse "unused preconnect" warnings and free up slots.
            link.rel = 'dns-prefetch';
            link.href = domain;
            document.head.appendChild(link);
        });

        const injectAll = () => {
            // -- GTM --
            if (gtmId && !document.getElementById('gtm-script')) {
                const script = document.createElement('script');
                script.id = 'gtm-script';
                script.async = true;
                script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;
                document.head.appendChild(script);
            }

            // -- Google Analytics --
            const gaConfig = integration?.analytics?.find(a => a.provider === 'GOOGLE_ANALYTICS');
            if (gaConfig && !document.getElementById('ga-script')) {
                const script = document.createElement('script');
                script.id = 'ga-script';
                script.async = true;
                script.src = `https://www.googletagmanager.com/gtag/js?id=${gaConfig.trackingId}`;
                document.head.appendChild(script);

                const gtag = (...args: any[]) => window.dataLayer.push(args);
                gtag('js', new Date());
                gtag('config', gaConfig.trackingId);
            }

            // -- Facebook Pixel --
            const fbConfig = integration?.analytics?.find(a => a.provider === 'FACEBOOK_PIXEL');
            if (fbConfig && !document.getElementById('fb-pixel-script')) {
                const script = document.createElement('script');
                script.id = 'fb-pixel-script';
                script.async = true;
                script.src = 'https://connect.facebook.net/en_US/fbevents.js';
                document.head.appendChild(script);
                window.fbq?.('init', fbConfig.trackingId);
                window.fbq?.('track', 'PageView');
            }
        };

        const runWhenIdle = (task: () => void) => {
            if ('requestIdleCallback' in window) {
                (window as any).requestIdleCallback(task, { timeout: 3500 });
            } else {
                setTimeout(task, 2500);
            }
        };

        if (document.readyState === 'complete') {
            runWhenIdle(injectAll);
        } else {
            const handleLoad = () => runWhenIdle(injectAll);
            window.addEventListener('load', handleLoad, { once: true });
            return () => window.removeEventListener('load', handleLoad);
        }
    }, [gtmId, integration]);

    // 3. Track Route Changes
    useEffect(() => {
        if (!integration?.analytics) return;

        if (window.dataLayer && integration.analytics.some(a => a.provider === 'GOOGLE_ANALYTICS')) {
            window.dataLayer.push({
                event: 'page_view',
                page_path: location.pathname + location.search
            });
        }

        if (window.fbq && integration.analytics.some(a => a.provider === 'FACEBOOK_PIXEL')) {
            window.fbq('track', 'PageView');
        }
    }, [location.pathname, location.search, integration]);

    return null;
};
